import Button from '../Button';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { getAuthentication } from '@/hooks/useAuthentication';
import { useResponsiveImage } from '@/utils/useResponsiveImage';
import { useMemo } from 'react';

interface OnBoardingPageProp {
  logo?: {
    url: string;
    alternativeText?: string | null;
  } | null;
}

interface OnBoardingContainerProps {
  page?: OnBoardingPageProp | null;
  image?: string;
  title: string;
  description?: string;
  step?: number;
  button: {
    text: string;
    onClick: () => void;
  };
  content?: React.ReactNode;
}

export default function OnBoardingContainer({
  page,
  image,
  title,
  description,
  step,
  button,
  content,
}: OnBoardingContainerProps) {
  const navigate = useNavigate();
  const { src } = useResponsiveImage(image ? { url: image, alternativeText: '' } : undefined);

  const logoUrl = useMemo(() => {
    const url = page?.logo?.url;
    if (!url) return '';
    if (url.startsWith('http')) return url;
    return `${import.meta.env.VITE_API_URL || ''}${url}`;
  }, [page?.logo?.url]);

  const resolvedImageSrc = useMemo(() => {
    if (!src) return '';
    if (src.startsWith('http')) return src;
    return `${import.meta.env.VITE_API_URL || ''}${src}`;
  }, [src]);

  if (!title) {
    return null;
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.7 }}
      className="flex flex-col items-center justify-center">
      <div className="my-10 flex flex-col items-center justify-center">
        <p className="text-sh-md font-bold">Welcome to</p>
        {logoUrl && <img src={logoUrl} alt="onboarding-bg" width={250} />}
      </div>

      <div className="grid-cols1 grid items-center justify-center gap-6 md:grid-cols-2">
        {image && resolvedImageSrc && (
          <img
            src={resolvedImageSrc}
            alt=""
            width={430}
            height={287}
            className="mb-7 transition-all duration-300 ease-in"
          />
        )}

        <div className="flex h-full flex-col justify-center">
          <div>
            <h1 className="mb-4 font-haffer text-sh-28-32 font-bold">{title}</h1>
            {!content && (
              <div className="mb-4">
                <p className="font-haffer text-sh-14-18">{description}</p>
              </div>
            )}

            {content && content}
            {(step || 0) > 0 && (
              <Button
                theme="outline"
                onClick={() => {
                  const stepUrl = `/on-boarding/step/${step || 1}`;
                  localStorage.setItem(`current-step-${getAuthentication()?.user?.id}`, stepUrl);
                  navigate(stepUrl);
                }}
                className="mb-3 mr-sh-15">
                Back
              </Button>
            )}
            <Button theme="solid" onClick={button.onClick}>
              {button.text}
            </Button>
          </div>
        </div>
      </div>
    </motion.div>
  );
}
